import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query/react';
import { DEFAULT_WRITE_MODEL, isJobDeletable, Job, ProblemCode } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { DifficultyMeter, LanguageCode, RelativeTime, TaskTypeBadge } from '../../task/TaskBadges';
import { useListTasksInfiniteQuery } from '../../task/taskEndpoints';
import { JobChunkTable } from '../JobChunkTable';
import { JobCostCard } from '../JobCostCard';
import { JobStatusPill } from '../JobBadges';
import { jobDisplayTitle, jobIcon, plannedCount, truncate } from '../jobDisplay';
import { useCancelJobMutation, useDeleteJobMutation, useGetJobQuery } from '../jobEndpoints';

/** The progress bar's class for the job's status: animated while running, tinted once it ends. */
const progressBarClass = (status: Job['status']): string => {
  switch (status) {
    case 'running':
      return 'progress-bar progress-bar-striped progress-bar-animated';
    case 'done':
      return 'progress-bar bg-success';
    case 'failed':
      return 'progress-bar bg-danger';
    default:
      return 'progress-bar';
  }
};

/**
 * QZ-D20, QZ-D49, QZ-D55, QZ-D56, QZ-D57, QZ-D106, PDG-D10: a job's progress page at
 * `/jobs/:jobId`. It polls while the job is active, shows its chunks, latest drafts and cost,
 * offers cancel while the job is planning or running, and offers Delete once the job has ended.
 */
export const JobPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [pollingInterval, setPollingInterval] = useState(5_000);
  const {
    data: job,
    error,
    isLoading,
    refetch: refetchJob,
  } = useGetJobQuery(jobId ?? skipToken, { pollingInterval, skipPollingIfUnfocused: true });

  const loadErrorCode = problemCode(error);
  const notFound =
    !job &&
    !!error &&
    (loadErrorCode === 'JobNotFound' || (error as FetchBaseQueryError).status === 404);

  // QZ-D106: a just-cancelled job may still have a chunk finishing the step that was in flight
  // (QZ-D57), so polling keeps running past an ended status until the job becomes deletable.
  const deletable = !!job && isJobDeletable(job);
  const loaded = !!job;
  useEffect(() => {
    if (notFound || deletable) {
      setPollingInterval(0);
    } else if (loaded) {
      setPollingInterval(5_000);
    }
  }, [loaded, notFound, deletable]);

  const isPlanning = job?.status === 'planning';
  const tasksQuery = useListTasksInfiniteQuery(
    jobId && job && job.status !== 'planning' ? { status: 'draft', jobId } : skipToken
  );
  const drafts = tasksQuery.data?.pages[0]?.tasks.slice(0, 5) ?? [];

  // QZ-D59: a poll's new job snapshot may carry freshly drafted tasks; the drafts list has no
  // tag linking it to the job, so it is nudged with an explicit refetch rather than invalidation.
  const prevDrafted = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!job) {
      return;
    }
    const sum = job.chunks.reduce((total, chunk) => total + chunk.drafted, 0);
    const prev = prevDrafted.current;
    if (!isPlanning && !tasksQuery.isUninitialized && prev !== undefined && prev !== sum) {
      tasksQuery.refetch();
    }
    prevDrafted.current = sum;
  }, [job]);

  const [cancelJob, { isLoading: cancelling }] = useCancelJobMutation();
  const [confirming, setConfirming] = useState(false);
  const [cancelError, setCancelError] = useState<ProblemCode | 'unknown' | undefined>(undefined);

  const [deleteJob, { isLoading: deleting }] = useDeleteJobMutation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<ProblemCode | 'unknown' | undefined>(undefined);

  // Opening either confirmation clears the other's error (QZ-D106).
  const openCancelConfirm = () => {
    setDeleteError(undefined);
    setConfirming(true);
  };
  const openDeleteConfirm = () => {
    setCancelError(undefined);
    setConfirmingDelete(true);
  };

  const handleCancel = async () => {
    if (!jobId) {
      return;
    }
    setCancelError(undefined);
    try {
      await cancelJob(jobId).unwrap();
      setConfirming(false);
    } catch (thrown) {
      const code = problemCode(thrown);
      setCancelError(code ?? 'unknown');
      if (code === 'JobEnded') {
        refetchJob();
      }
    }
  };

  const handleDelete = async () => {
    if (!jobId) {
      return;
    }
    setDeleteError(undefined);
    try {
      await deleteJob(jobId).unwrap();
      // QZ-D106: navigate at once, before anything can refetch the now-deleted job.
      navigate('/jobs', { replace: true });
    } catch (thrown) {
      const fetchError = thrown as FetchBaseQueryError;
      const code = problemCode(thrown);
      if (code === 'JobNotFound' || fetchError.status === 404) {
        // Already gone, deleted in another tab: leave as if this delete had succeeded.
        navigate('/jobs', { replace: true });
        return;
      }
      setDeleteError(code ?? 'unknown');
      if (code === 'JobNotEnded') {
        refetchJob();
      }
    }
  };

  const openTask = (taskId: string) => navigate(`/tasks/${taskId}`);

  const finishedPercent =
    job && job.chunks.length > 0
      ? Math.round(
          (100 *
            job.chunks.filter((chunk) => chunk.status === 'done' || chunk.status === 'failed')
              .length) /
            job.chunks.length
        )
      : 0;

  // PDG-D10, QZ-D73: the plan may have chosen fewer tasks than asked for a PDF job.
  const fewer = !!job && job.chunks.length > 0 && plannedCount(job) < job.taskCount;

  // PTX-D5: numbers in the PDF range line are localised to the UI language.
  const numberFormat = new Intl.NumberFormat(i18n.language);

  return (
    <main className="qz-main">
      <div className="qz-container">
        <Link className="qz-back" to="/jobs">
          <Icon name="arrow-left" /> {t('job.page.back')}
        </Link>

        {isLoading && <p className="qz-muted">{t('job.page.loading')}</p>}

        {!job && !isLoading && error && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>
              {t(
                notFound
                  ? 'job.page.notFound'
                  : loadErrorCode
                    ? `error.${loadErrorCode}`
                    : 'error.unknown'
              )}
            </div>
          </div>
        )}

        {job && (
          <>
            <div className="qz-page-header align-items-start">
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <JobStatusPill job={job} />
                  <LanguageCode language={job.language} />
                  <span className="qz-small qz-muted">
                    <Icon name={jobIcon(job)} /> {t('job.field.created')}{' '}
                    <RelativeTime iso={job.createdAt} />
                  </span>
                </div>
                <h1 style={{ maxWidth: '44rem' }}>{jobDisplayTitle(job, 120)}</h1>
              </div>
              <div className="d-flex gap-2">
                {job.status !== 'planning' && (
                  <Link className="btn btn-primary" to={`/tasks?jobId=${jobId}`}>
                    {t('job.page.reviewDrafts')}
                  </Link>
                )}
                {(job.status === 'planning' || job.status === 'running') &&
                  (confirming ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="qz-small">{t('job.page.confirmCancel')}</span>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={cancelling}
                        onClick={handleCancel}
                      >
                        {t('job.page.cancelJob')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={cancelling}
                        onClick={() => setConfirming(false)}
                      >
                        {t('job.page.keepRunning')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={openCancelConfirm}
                    >
                      {t('job.page.cancelJob')}
                    </button>
                  ))}
                {deletable &&
                  (confirmingDelete ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="qz-small">{t('job.page.confirmDelete')}</span>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={deleting}
                        onClick={handleDelete}
                      >
                        {t('job.page.deleteJob')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={deleting}
                        onClick={() => setConfirmingDelete(false)}
                      >
                        {t('job.page.keepJob')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={openDeleteConfirm}
                    >
                      {t('job.page.deleteJob')}
                    </button>
                  ))}
              </div>
            </div>

            {cancelError && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div>{t(cancelError === 'unknown' ? 'error.unknown' : `error.${cancelError}`)}</div>
              </div>
            )}

            {deleteError && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div>{t(deleteError === 'unknown' ? 'error.unknown' : `error.${deleteError}`)}</div>
              </div>
            )}

            {job.status === 'failed' && (
              <div className="alert alert-danger">
                <Icon name="circle-exclamation" />
                <div className="qz-mono qz-small">{job.error ?? t('job.status.failed')}</div>
              </div>
            )}

            <div className="row g-4">
              <div className="col-lg-8">
                <div className="qz-card">
                  <div className="qz-card-body">
                    {job.chunks.length === 0 ? (
                      // A job that ended before its plan was saved has no chunks either.
                      <span className="fw-semibold">
                        {t(isPlanning ? 'job.page.planning' : 'job.page.noPlan')}
                      </span>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-baseline mb-2">
                          <span className="fw-semibold">
                            {t('job.page.progress', {
                              done: job.chunks.filter((chunk) => chunk.status === 'done').length,
                              total: job.chunks.length,
                            })}
                          </span>
                        </div>
                        <div
                          className="progress"
                          role="progressbar"
                          aria-valuenow={finishedPercent}
                        >
                          <div
                            className={progressBarClass(job.status)}
                            style={{ width: `${finishedPercent}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {job.chunks.length > 0 && <JobChunkTable chunks={job.chunks} />}
                </div>

                {!isPlanning && (
                  <div className="qz-card">
                    <div className="qz-card-header">
                      <h3>{t('job.page.latestDrafts')}</h3>
                      <Link className="qz-small" to={`/tasks?jobId=${jobId}`}>
                        {t('job.page.allDrafts')}
                      </Link>
                    </div>
                    {drafts.length === 0 ? (
                      <div className="qz-empty">{t('job.page.noDrafts')}</div>
                    ) : (
                      <table className="table table-hover table-clickable align-middle">
                        <tbody>
                          {drafts.map((draftTask) => {
                            const text =
                              draftTask.translations[draftTask.originalLanguage]?.text ?? '';
                            return (
                              <tr
                                key={draftTask.taskId}
                                role="button"
                                tabIndex={0}
                                onClick={() => openTask(draftTask.taskId)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    openTask(draftTask.taskId);
                                  } else if (e.key === ' ') {
                                    e.preventDefault();
                                    openTask(draftTask.taskId);
                                  }
                                }}
                              >
                                <td className="fw-semibold">{truncate(text, 80)}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-1">
                                    {draftTask.taskTypes.map((taskType) => (
                                      <TaskTypeBadge key={taskType} type={taskType} />
                                    ))}
                                  </div>
                                </td>
                                <td className="text-end">
                                  <DifficultyMeter difficulty={draftTask.difficulty} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>

              <div className="col-lg-4">
                <JobCostCard job={job} />

                {job.method === 'prompt' ? (
                  <div className="qz-card">
                    <div className="qz-card-header">
                      <h3>{t('job.page.request')}</h3>
                    </div>
                    <div className="qz-card-body qz-small">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{job.request}</p>
                      <dl className="qz-kv">
                        <dt>{t('job.field.language')}</dt>
                        <dd>{t(`task.language.${job.language}`)}</dd>
                        <dt>{t('job.field.taskCount')}</dt>
                        <dd>{job.taskCount}</dd>
                        <dt>{t('job.field.writeModel')}</dt>
                        <dd>{t(`job.writeModel.${job.writeModel ?? DEFAULT_WRITE_MODEL}`)}</dd>
                        <dt>{t('job.field.sourceSites')}</dt>
                        <dd>
                          {job.sourceSites.length > 0
                            ? job.sourceSites.join(', ')
                            : t('job.field.openWeb')}
                        </dd>
                      </dl>
                    </div>
                  </div>
                ) : (
                  // PDG-D10, QZ-D91, QZ-D73: a material job's side card (PDF or photos) shows
                  // the material, instructions and how many tasks the plan chose, instead of a
                  // request and source sites.
                  <div className="qz-card">
                    <div className="qz-card-header">
                      <h3>{t('job.page.material')}</h3>
                    </div>
                    <div className="qz-card-body qz-small">
                      {job.studyMaterial && (
                        <div className="qz-file">
                          <Icon name={jobIcon(job)} />
                          <div>
                            <div className="fw-semibold">{job.studyMaterial.name}</div>
                            {job.studyMaterial.section && (
                              // QZ-D100: the section is shown beside the document, here only.
                              <div>
                                <span className="qz-muted">{t('job.page.section')}</span>{' '}
                                {job.studyMaterial.section}
                              </div>
                            )}
                            {job.pageRange !== undefined &&
                              job.pdfPageCount !== undefined &&
                              job.textChars !== undefined &&
                              (job.method === 'pdf' ? (
                                // PTX-D5: the job stores its page range, the PDF's page count
                                // and its text's character count; show all three.
                                <div className="qz-muted">
                                  {t('job.page.pdfRange', {
                                    from: numberFormat.format(job.pageRange.from),
                                    to: numberFormat.format(job.pageRange.to),
                                    count: numberFormat.format(job.pdfPageCount),
                                    chars: numberFormat.format(job.textChars),
                                  })}
                                </div>
                              ) : (
                                // PHO-D5: a photo job's pdfPageCount counts its photos, and its
                                // range is always the whole set, so no range is shown. count is
                                // passed as a number, not formatted, so i18next can pick the
                                // plural form.
                                <div className="qz-muted">
                                  {t('job.page.photos', {
                                    count: job.pdfPageCount,
                                    chars: numberFormat.format(job.textChars),
                                  })}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      {job.instructions && (
                        <>
                          <div className="qz-label">{t('job.field.instructions')}</div>
                          <p style={{ whiteSpace: 'pre-wrap' }}>{job.instructions}</p>
                        </>
                      )}
                      <dl className="qz-kv">
                        <dt>{t('job.field.language')}</dt>
                        <dd>{t(`task.language.${job.language}`)}</dd>
                        <dt>{t('job.field.taskCount')}</dt>
                        <dd>
                          {fewer
                            ? t('job.page.planned', {
                                planned: plannedCount(job),
                                taskCount: job.taskCount,
                              })
                            : job.taskCount}
                        </dd>
                        <dt>{t('job.field.writeModel')}</dt>
                        <dd>{t(`job.writeModel.${job.writeModel ?? DEFAULT_WRITE_MODEL}`)}</dd>
                      </dl>
                      {fewer && job.planNote && (
                        <p className="qz-muted mt-2 mb-0">
                          {t('job.page.plannedFewer', { note: job.planNote })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default JobPage;
